import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../../services/api';

// State every student screen shares, fetched by the shell instead of per page:
//   player   GET /portal/player-profile  (xp, level, levelProgress, streak, missions, achievements, avatar_id,
//            personalized_journey_eligible)
//   unread   GET /portal/notifications/unread-count
// plus the profile-sheet open state. Nothing is invented: until a request resolves the value is null and the UI
// renders an honest skeleton/omits it.
//
// audit ST-10/ST-11: this used to fetch ONCE per session (empty deps), so `missions`/
// `personalized_journey_eligible` went stale the moment a student did anything that changed them and
// never self-corrected without a full reload. `TakeTest`/`MockTestTake` (where a test/practice session
// is actually submitted) intentionally render OUTSIDE this provider (full-screen exam, see StudentShell's
// own comment) so they can't call `refreshPlayer()` directly — but every one of their exit points lands
// back on a shell-wrapped route (Home, Mock setup, Tests, Analytics). Refreshing on every in-shell
// pathname change catches all of those re-entries; MistakeBank additionally calls `refreshPlayer()`
// itself for the one activity (resolving a mistake via the retry modal) that completes without a
// navigation at all.
const StudentContext = createContext(null);

export function StudentProvider({ children }) {
  const [player, setPlayer] = useState(null);
  const [playerError, setPlayerError] = useState(false);
  const [unread, setUnread] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  const refreshPlayer = useCallback(async () => {
    try {
      const res = await API.get('/portal/player-profile');
      setPlayer(res.data);
      setPlayerError(false);
      return res.data;
    } catch (err) {
      setPlayerError(true);
      return null;
    }
  }, []);

  const refreshUnread = useCallback(async () => {
    try {
      const res = await API.get('/portal/notifications/unread-count');
      setUnread(res.data.unread);
    } catch { /* the bell simply shows no badge */ }
  }, []);

  const location = useLocation();
  // `refreshPlayer`/`refreshUnread` are stable (useCallback with `[]` deps) so listing them here
  // doesn't add re-fetches — this is deliberately keyed on pathname only: a query-string-only change
  // (e.g. an admin-style `?tab=`) is not a re-entry into the shell and should not re-fetch.
  useEffect(() => { refreshPlayer(); refreshUnread(); }, [location.pathname, refreshPlayer, refreshUnread]);

  const setAvatarId = useCallback((avatar_id) => setPlayer(p => (p ? { ...p, avatar_id } : p)), []);

  const value = useMemo(() => ({
    player, playerError, refreshPlayer, setAvatarId, unread, setUnread, refreshUnread,
    profileOpen, openProfile: () => setProfileOpen(true), closeProfile: () => setProfileOpen(false),
  }), [player, playerError, refreshPlayer, setAvatarId, unread, refreshUnread, profileOpen]);

  return <StudentContext.Provider value={value}>{children}</StudentContext.Provider>;
}

/** Outside the shell (e.g. a full-screen exam) this returns inert defaults instead of throwing. */
export function useStudent() {
  const ctx = useContext(StudentContext);
  if (ctx) return ctx;
  return { player: null, playerError: false, refreshPlayer: async () => null, setAvatarId: () => {}, unread: null, setUnread: () => {}, refreshUnread: async () => {}, profileOpen: false, openProfile: () => {}, closeProfile: () => {} };
}
